import { apiFetch, apiJson } from '@/lib/apiFetch';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

/** Prepend API_URL to relative paths so images resolve to the backend, not Vite dev server */
const resolveUrl = (url: string): string =>
  url.startsWith('/') ? `${API_URL}${url}` : url;

const normalizeAsset = (raw: Asset): Asset => ({
  ...raw,
  url: resolveUrl(raw.url),
});

export interface AssetUsage {
  id: string;
  name: string;
}

export interface AssetDeleteResult {
  conflict: boolean;
  usedBy?: AssetUsage[];
}

export interface AssetCategory {
  id: string;
  name: string;
  slug: string;
  sortOrder: number;
}

export interface Asset {
  id: string;
  name: string;
  url: string;
  originalName: string;
  mimeType: string;
  size: number;
  width: number;
  height: number;
  hasTransparency: boolean | null;
  createdAt: string;
  source: 'user' | 'printtat';
  tags: string[];
  uploadedByUserId: string;
  uploadedBy: { id: string; name: string; email: string } | null;
  categoryId: string | null;
  categoryName: string | null;
}

export interface PaginatedMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PaginatedAssets {
  data: Asset[];
  meta: PaginatedMeta;
}

export interface AssetListParams {
  source?: 'user' | 'printtat';
  category?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export const assetsApi = {
  async listCategories(): Promise<AssetCategory[]> {
    // Public endpoint — skipAuth to avoid sending a stale token.
    return apiJson<AssetCategory[]>('/asset-categories', { skipAuth: true });
  },

  async list(source?: 'user' | 'printtat', category?: string): Promise<Asset[]> {
    const params = new URLSearchParams();
    if (source) params.set('source', source);
    if (category) params.set('category', category);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const json = await apiJson<PaginatedAssets>(`/assets${qs}`);
    return json.data.map(normalizeAsset);
  },

  async listPaginated(listParams: AssetListParams): Promise<PaginatedAssets> {
    const params = new URLSearchParams();
    if (listParams.source) params.set('source', listParams.source);
    if (listParams.category) params.set('category', listParams.category);
    if (listParams.search) params.set('search', listParams.search);
    if (listParams.page) params.set('page', String(listParams.page));
    if (listParams.limit) params.set('limit', String(listParams.limit));
    const qs = params.toString() ? `?${params.toString()}` : '';
    const json = await apiJson<PaginatedAssets>(`/assets${qs}`);
    return { data: json.data.map(normalizeAsset), meta: json.meta };
  },

  async upload(file: File, meta: { width: number; height: number }): Promise<Asset> {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('width', String(meta.width));
    fd.append('height', String(meta.height));
    const data = await apiJson<Asset>('/assets/upload', {
      method: 'POST',
      body: fd,
    });
    return normalizeAsset(data);
  },

  async uploadBulk(files: File[], metas: { width: number; height: number }[]): Promise<Asset[]> {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    fd.append('metas', JSON.stringify(metas));
    const data = await apiJson<Asset[]>('/assets/upload-bulk', {
      method: 'POST',
      body: fd,
    });
    return data.map(normalizeAsset);
  },

  async rename(id: string, name: string): Promise<Asset> {
    const data = await apiJson<Asset>(`/assets/${id}`, {
      method: 'PATCH',
      body: { name },
    });
    return normalizeAsset(data);
  },

  async delete(id: string, force = false): Promise<AssetDeleteResult> {
    const res = await apiFetch(`/assets/${id}${force ? '?force=true' : ''}`, {
      method: 'DELETE',
    });
    if (res.status === 409) {
      const data = await res.json();
      return { conflict: true, usedBy: data.usedBy ?? [] };
    }
    if (!res.ok) throw new Error('Delete failed');
    return { conflict: false };
  },
};
